import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PackageIcon, WarningIcon, PlusIcon, TrashIcon, PencilSimpleIcon, CarrotIcon } from "@phosphor-icons/react";

const REASON_LABEL = {
  purchase: "Purchase / stock-in",
  return: "Return",
  wastage: "Wastage / spoilage",
  correction: "Correction",
  transfer_in: "Transfer in",
  transfer_out: "Transfer out",
};

export function StockManager({ activeId, menu, onChanged }) {
  const stockItems = (menu || []).filter((m) => m.track_stock);
  const [selected, setSelected] = useState("");
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("purchase");
  const [notes, setNotes] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [movements, setMovements] = useState([]);
  const [takeOpen, setTakeOpen] = useState(false);
  const [takeEntries, setTakeEntries] = useState({});

  // Raw ingredient items (Potato, Oil, Chicken, Salt…)
  const [rawItems, setRawItems] = useState([]);
  const [rawForm, setRawForm] = useState({ name: "", unit: "kg", stock: 0, min_stock: 0, cost_per_unit: 0 });
  const [rawEditing, setRawEditing] = useState(null);
  const UNITS = ["kg", "g", "L", "ml", "pcs", "packet", "bottle", "dozen"];

  const load = () => activeId && api.get("/pos/stock/movements", { params: { property_id: activeId, limit: 500 } })
    .then((r) => setMovements(r.data))
    .catch(() => {});
  const loadRaw = () => activeId && api.get("/pos/stock-items", { params: { property_id: activeId } })
    .then((r) => setRawItems(r.data))
    .catch(() => {});
  useEffect(() => { load(); loadRaw(); /* eslint-disable-next-line */ }, [activeId]);

  const saveRaw = async () => {
    if (!rawForm.name.trim()) return toast.error("Item name required");
    try {
      const body = {
        ...rawForm,
        stock: Number(rawForm.stock) || 0,
        min_stock: Number(rawForm.min_stock) || 0,
        cost_per_unit: Number(rawForm.cost_per_unit) || 0,
      };
      if (rawEditing) {
        await api.patch(`/pos/stock-items/${rawEditing.id}`, body);
        toast.success(`${rawForm.name} updated`);
      } else {
        await api.post("/pos/stock-items", { property_id: activeId, ...body });
        toast.success(`${rawForm.name} added`);
      }
      setRawForm({ name: "", unit: "kg", stock: 0, min_stock: 0, cost_per_unit: 0 });
      setRawEditing(null);
      loadRaw();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const editRaw = (r) => { setRawEditing(r); setRawForm({ name: r.name, unit: r.unit, stock: r.stock, min_stock: r.min_stock, cost_per_unit: r.cost_per_unit || 0 }); };
  const cancelRawEdit = () => { setRawEditing(null); setRawForm({ name: "", unit: "kg", stock: 0, min_stock: 0, cost_per_unit: 0 }); };
  const deleteRaw = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    await api.delete(`/pos/stock-items/${r.id}`);
    toast.success("Deleted"); loadRaw();
  };
  const adjustRaw = async (r, deltaVal) => {
    const d = Number(deltaVal);
    if (!d) return;
    await api.post("/pos/stock-items/adjust", { stock_item_id: r.id, delta: d, reason: d > 0 ? "purchase" : "wastage" });
    toast.success(`${r.name} ${d > 0 ? "+" : ""}${d} ${r.unit}`);
    loadRaw();
  };

  const adjust = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Pick an item");
    const d = Number(delta);
    if (!d) return toast.error("Delta must be non-zero");
    try {
      await api.post("/pos/stock/adjust", {
        menu_item_id: selected,
        delta: d,
        reason,
        notes: notes || null,
        unit_cost: unitCost === "" ? null : Number(unitCost),
      });
      toast.success("Stock adjusted");
      setDelta(0); setNotes(""); setUnitCost("");
      onChanged?.();
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submitTake = async () => {
    const entries = Object.entries(takeEntries)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined)
      .map(([menu_item_id, counted_qty]) => ({ menu_item_id, counted_qty: Number(counted_qty) }));
    if (entries.length === 0) return toast.error("Enter counted quantities for at least one item");
    try {
      const r = await api.post("/pos/stock/bulk-take", { property_id: activeId, entries });
      toast.success(`${r.data.count_adjusted} stock adjustments applied`);
      setTakeOpen(false); setTakeEntries({});
      onChanged?.();
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const low = stockItems.filter((m) => Number(m.stock ?? 0) <= Number(m.min_stock ?? 5));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="label-overline">Store keeping</p>
              <h3 className="font-display text-xl">Adjust stock</h3>
              <p className="text-xs text-muted-foreground mt-1">Record purchases, wastage, returns and corrections. Sales auto-decrement.</p>
            </div>
            <Dialog open={takeOpen} onOpenChange={setTakeOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="stock-take-btn"><PackageIcon size={12} className="mr-1" /> Bulk stock-take</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Bulk stock-take</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground">Enter the counted quantity per item — the system computes the delta and creates the correction lines.</p>
                <div className="max-h-96 overflow-auto mt-3 divide-y divide-border">
                  {stockItems.length === 0 && <p className="text-sm italic text-muted-foreground py-6 text-center">No stock-tracked items.</p>}
                  {stockItems.map((m) => (
                    <div key={m.id} className="grid grid-cols-4 gap-2 py-2 items-center" data-testid={`stock-take-row-${m.id}`}>
                      <div className="text-sm">{m.name}</div>
                      <div className="text-xs text-muted-foreground">Book: <span className="font-mono-plex">{m.stock ?? 0}</span></div>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Counted"
                        value={takeEntries[m.id] ?? ""}
                        onChange={(e) => setTakeEntries({ ...takeEntries, [m.id]: e.target.value })}
                        className="h-9 col-span-2"
                        data-testid={`stock-take-input-${m.id}`}
                      />
                    </div>
                  ))}
                </div>
                <DialogFooter><Button onClick={submitTake} data-testid="stock-take-submit">Apply corrections</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <form onSubmit={adjust} className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Item</Label>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger className="mt-1" data-testid="stock-item-select"><SelectValue placeholder="Pick a stock-tracked item…" /></SelectTrigger>
                <SelectContent>
                  {stockItems.length === 0 && <SelectItem value="__none" disabled>No stock-tracked items — enable tracking on menu items first</SelectItem>}
                  {stockItems.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name} · stock {m.stock ?? 0}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Delta (± qty)</Label>
              <Input type="number" step="1" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 50 or -3" className="mt-1" data-testid="stock-delta" />
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="mt-1" data-testid="stock-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(REASON_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" data-testid="stock-adjust-submit">Adjust</Button>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Notes</Label>
              <Input placeholder="Vendor, invoice #, reason…" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="stock-notes" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Unit cost (₹) — optional</Label>
              <Input type="number" step="0.01" placeholder="0.00" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} className="mt-1" data-testid="stock-unit-cost" />
            </div>
          </form>
        </Card>

        <Card className="p-5" data-testid="stock-low-card">
          <div className="flex items-center gap-2">
            <WarningIcon size={18} weight="duotone" className="text-[hsl(var(--warning))]" />
            <p className="label-overline">Low stock ({low.length})</p>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Items at or below their <em>min_stock</em>.</p>
          <ul className="mt-3 space-y-1 text-sm max-h-56 overflow-y-auto">
            {low.length === 0 && <li className="italic text-muted-foreground text-xs">Everything is above threshold.</li>}
            {low.map((m) => (
              <li key={m.id} className="flex justify-between border-b border-border/60 py-1" data-testid={`stock-low-${m.id}`}>
                <span>{m.name}</span>
                <span className="font-mono-plex">
                  <span className="text-destructive">{m.stock ?? 0}</span>
                  <span className="text-muted-foreground"> / {m.min_stock ?? 5}</span>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card data-testid="stock-raw-panel">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <CarrotIcon size={20} weight="duotone" className="text-primary" />
          <div className="flex-1">
            <p className="label-overline">Raw ingredients ({rawItems.length})</p>
            <p className="text-xs text-muted-foreground">Add items like Potato, Oil, Chicken, Salt. Link them to menu items via the Recipe builder — orders auto-deduct.</p>
          </div>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-6 gap-2 border-b border-border bg-muted/20">
          <Input placeholder="Name (e.g. Potato)" value={rawForm.name} onChange={(e) => setRawForm({ ...rawForm, name: e.target.value })} className="md:col-span-2" data-testid="raw-form-name" />
          <Select value={rawForm.unit} onValueChange={(v) => setRawForm({ ...rawForm, unit: v })}>
            <SelectTrigger data-testid="raw-form-unit"><SelectValue /></SelectTrigger>
            <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" placeholder="Stock" value={rawForm.stock} onChange={(e) => setRawForm({ ...rawForm, stock: e.target.value })} data-testid="raw-form-stock" />
          <Input type="number" placeholder="Min stock" value={rawForm.min_stock} onChange={(e) => setRawForm({ ...rawForm, min_stock: e.target.value })} data-testid="raw-form-min" />
          <div className="flex gap-1">
            <Button className="flex-1" onClick={saveRaw} data-testid="raw-form-save">{rawEditing ? "Save" : "Add"}</Button>
            {rawEditing && <Button variant="ghost" onClick={cancelRawEdit} data-testid="raw-form-cancel">Cancel</Button>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="p-3">Item</th>
                <th className="p-3">Unit</th>
                <th className="p-3 text-right">On hand</th>
                <th className="p-3 text-right">Min</th>
                <th className="p-3 text-right">Cost</th>
                <th className="p-3 text-right">Quick +/-</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rawItems.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground italic">No raw ingredients yet — add Potato, Oil, Chicken, Salt… in the form above.</td></tr>
              )}
              {rawItems.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`raw-row-${r.id}`}>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 text-xs text-muted-foreground">{r.unit}</td>
                  <td className={`p-3 text-right font-mono-plex ${Number(r.stock) <= Number(r.min_stock || 0) ? "text-[hsl(var(--warning))]" : ""}`}>{r.stock}</td>
                  <td className="p-3 text-right text-xs text-muted-foreground">{r.min_stock}</td>
                  <td className="p-3 text-right text-xs font-mono-plex">₹{r.cost_per_unit || 0}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => adjustRaw(r, 1)} data-testid={`raw-plus-${r.id}`}>+1</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => adjustRaw(r, 5)} data-testid={`raw-plus5-${r.id}`}>+5</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => adjustRaw(r, -1)} data-testid={`raw-minus-${r.id}`}>-1</Button>
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => editRaw(r)} data-testid={`raw-edit-${r.id}`}><PencilSimpleIcon size={11} /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteRaw(r)} data-testid={`raw-delete-${r.id}`}><TrashIcon size={11} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <div className="p-4 border-b border-border">
          <p className="label-overline">Movement history</p>
          <h3 className="font-display text-lg">Recent stock movements</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3">When</th>
                <th className="text-left p-3">Item</th>
                <th className="text-right p-3">Δ</th>
                <th className="text-right p-3">Before → After</th>
                <th className="text-left p-3">Reason</th>
                <th className="text-left p-3">By</th>
                <th className="text-left p-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground italic">No movements yet — adjust some stock above.</td></tr>
              )}
              {movements.map((mv) => (
                <tr key={mv.id} className="border-t border-border" data-testid={`stock-movement-${mv.id}`}>
                  <td className="p-3 text-xs font-mono-plex">{mv.created_at?.slice(0, 19).replace("T", " ")}</td>
                  <td className="p-3">{mv.menu_item_name}</td>
                  <td className={`p-3 text-right font-mono-plex ${Number(mv.delta) > 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{Number(mv.delta) > 0 ? "+" : ""}{mv.delta}</td>
                  <td className="p-3 text-right text-xs font-mono-plex text-muted-foreground">{mv.before} → {mv.after}</td>
                  <td className="p-3 text-xs">{REASON_LABEL[mv.reason] || mv.reason}</td>
                  <td className="p-3 text-xs text-muted-foreground">{mv.actor_email}</td>
                  <td className="p-3 text-xs text-muted-foreground max-w-xs truncate">{mv.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
